/**
 * Pattern Clustering for Interview Facets
 *
 * Groups similar facets across interviews to identify emerging patterns.
 * Uses text similarity and facet type matching.
 */

import type {
  Interview,
  InputSource,
  Facet,
  FacetType,
  PatternCluster,
  ClusteringConfig,
} from '@/types/interviewer'
import { FACET_TYPE_CONFIG } from '@/types/interviewer'

const DEFAULT_CONFIG: ClusteringConfig = {
  minMentions: 2,
  minEvidenceStrength: 2,
  similarityThreshold: 0.4,
}

/**
 * Simple text similarity using word overlap (Jaccard index)
 */
function textSimilarity(a: string, b: string): number {
  const wordsA = new Set(a.toLowerCase().split(/\s+/).filter(w => w.length > 3))
  const wordsB = new Set(b.toLowerCase().split(/\s+/).filter(w => w.length > 3))

  if (wordsA.size === 0 || wordsB.size === 0) return 0

  const intersection = new Set([...wordsA].filter(x => wordsB.has(x)))
  const union = new Set([...wordsA, ...wordsB])

  return intersection.size / union.size
}

/**
 * Extract key terms from facet content for cluster naming
 */
function extractKeyTerms(facets: Facet[]): string[] {
  const wordCounts = new Map<string, number>()
  const stopWords = new Set([
    'the', 'and', 'for', 'that', 'this', 'with', 'from', 'have', 'has',
    'but', 'not', 'are', 'was', 'were', 'been', 'being', 'would', 'could',
    'should', 'they', 'them', 'their', 'what', 'when', 'where', 'which',
    'who', 'will', 'about', 'into', 'than', 'then', 'there', 'these',
    'just', 'also', 'only', 'very', 'more', 'some', 'such', 'because',
  ])

  for (const facet of facets) {
    const words = facet.content.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length > 3 && !stopWords.has(w))

    for (const word of words) {
      wordCounts.set(word, (wordCounts.get(word) || 0) + 1)
    }
  }

  return [...wordCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([word]) => word)
}

/**
 * Generate a human-readable cluster name
 */
function generateClusterName(facets: Facet[], facetType: FacetType): string {
  const keyTerms = extractKeyTerms(facets)
  const config = FACET_TYPE_CONFIG[facetType]
  const label = config?.label || facetType

  if (keyTerms.length > 0) {
    const capitalizedTerms = keyTerms
      .map(t => t.charAt(0).toUpperCase() + t.slice(1))
      .join(' + ')
    return `${label}: ${capitalizedTerms}`
  }

  return `${label} Pattern #${Date.now().toString(36).slice(-4)}`
}

/**
 * Cluster facets across sources/interviews by similarity
 */
export function clusterFacets(
  sources: (Interview | InputSource)[],
  config: ClusteringConfig = DEFAULT_CONFIG
): PatternCluster[] {
  // Collect all non-dismissed facets with their source IDs
  const facetsWithContext: Array<{ facet: Facet; interviewId: string }> = []

  for (const source of sources) {
    if (source.status !== 'analyzed') continue

    for (const facet of source.facets) {
      if (!facet.dismissed) {
        facetsWithContext.push({ facet, interviewId: source.id })
      }
    }
  }

  // Group by facet type first
  const byType = new Map<FacetType, Array<{ facet: Facet; interviewId: string }>>()
  for (const item of facetsWithContext) {
    const existing = byType.get(item.facet.type) || []
    existing.push(item)
    byType.set(item.facet.type, existing)
  }

  const clusters: PatternCluster[] = []

  // For each facet type, cluster similar facets
  for (const [facetType, items] of byType) {
    if (items.length < config.minMentions) continue

    // Simple greedy clustering
    const used = new Set<string>()
    const typeClus: Array<typeof items> = []

    for (const item of items) {
      if (used.has(item.facet.id)) continue

      // Start a new cluster with this facet
      const cluster = [item]
      used.add(item.facet.id)

      // Find similar facets
      for (const other of items) {
        if (used.has(other.facet.id)) continue

        const sim = textSimilarity(item.facet.content, other.facet.content)
        if (sim >= config.similarityThreshold) {
          cluster.push(other)
          used.add(other.facet.id)
        }
      }

      if (cluster.length >= config.minMentions) {
        typeClus.push(cluster)
      }
    }

    // Convert to PatternCluster objects
    for (const clusterItems of typeClus) {
      const facets = clusterItems.map(i => i.facet)
      const interviewIds = [...new Set(clusterItems.map(i => i.interviewId))]

      const avgConfidence = facets.reduce((sum, f) => sum + f.confidence, 0) / facets.length
      const avgEvidence = facets
        .filter(f => f.evidenceStrength !== undefined)
        .reduce((sum, f) => sum + (f.evidenceStrength || 0), 0) /
        (facets.filter(f => f.evidenceStrength !== undefined).length || 1)

      clusters.push({
        id: `cluster_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: generateClusterName(facets, facetType),
        description: facets[0].content, // Use first facet as representative
        facetIds: facets.map(f => f.id),
        interviewIds,
        mentionCount: facets.length,
        avgEvidenceStrength: avgEvidence,
        avgConfidence,
        ontologyLinks: [],
        emergingPattern: interviewIds.length >= 2, // Mentioned in multiple interviews
        suggestedEntityType: getSuggestedEntityType(facetType),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    }
  }

  // Sort by mention count descending
  return clusters.sort((a, b) => b.mentionCount - a.mentionCount)
}

/**
 * Map facet types to suggested ontology entity types
 */
function getSuggestedEntityType(facetType: FacetType): string | undefined {
  const config = FACET_TYPE_CONFIG[facetType]
  return config?.ontologyTypes?.[0]
}

/**
 * Search facets across all interviews
 */
export function searchFacets(
  interviews: Interview[],
  query: string
): Array<{ facet: Facet; interview: Interview; score: number }> {
  if (!query.trim()) return []

  const queryLower = query.toLowerCase()
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2)
  const results: Array<{ facet: Facet; interview: Interview; score: number }> = []

  for (const interview of interviews) {
    for (const facet of interview.facets) {
      if (facet.dismissed) continue

      const contentLower = facet.content.toLowerCase()
      const evidenceLower = facet.evidence.toLowerCase()

      // Calculate match score
      let score = 0

      // Exact phrase match in content
      if (contentLower.includes(queryLower)) {
        score += 10
      }

      // Exact phrase match in evidence
      if (evidenceLower.includes(queryLower)) {
        score += 5
      }

      // Word matches
      for (const word of queryWords) {
        if (contentLower.includes(word)) score += 2
        if (evidenceLower.includes(word)) score += 1
      }

      if (score > 0) {
        results.push({ facet, interview, score })
      }
    }
  }

  // Sort by score descending
  return results.sort((a, b) => b.score - a.score)
}

/**
 * Get aggregated statistics across all sources/interviews
 */
export function getAggregatedStats(sources: (Interview | InputSource)[]): {
  totalInterviews: number
  totalFacets: number
  avgMomTestScore: number
  avgEvidenceStrength: number
  facetsByType: Record<string, number>  // Use string for broader compatibility
  topProblems: Facet[]
} {
  const analyzed = sources.filter(s => s.status === 'analyzed')

  const allFacets = analyzed.flatMap(i => i.facets.filter(f => !f.dismissed))

  const facetsByType = {} as Record<FacetType, number>
  for (const facet of allFacets) {
    facetsByType[facet.type] = (facetsByType[facet.type] || 0) + 1
  }

  const momTestScores = analyzed
    .map(i => i.scores.momTestScore)
    .filter((s): s is number => s !== undefined)

  const evidenceScores = analyzed
    .map(i => i.scores.evidenceStrength)
    .filter((s): s is number => s !== undefined)

  // Get top problems by evidence strength
  const problems = allFacets
    .filter(f => f.type === 'problem')
    .sort((a, b) => (b.evidenceStrength || 0) - (a.evidenceStrength || 0))
    .slice(0, 5)

  return {
    totalInterviews: analyzed.length,
    totalFacets: allFacets.length,
    avgMomTestScore: momTestScores.length > 0
      ? momTestScores.reduce((a, b) => a + b, 0) / momTestScores.length
      : 0,
    avgEvidenceStrength: evidenceScores.length > 0
      ? evidenceScores.reduce((a, b) => a + b, 0) / evidenceScores.length
      : 0,
    facetsByType,
    topProblems: problems,
  }
}
